import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import LinearProgress from '@mui/material/LinearProgress';
import WeeklyShipService from './WeeklyShipService';

// D2U / air dealer-parcel import — a modal opened from the Weekly Ship Import page when
// the selected round is an air (D2U / ODA J4410) round. Upload a dealer parcelBox Excel;
// each row becomes an in-round box (matched + verified → priced) or a project-HOLD box,
// with images re-hosted in the background. Scanning the SMILE{boxNo}/{shipDate} QR to
// print a label happens on the Tracking-Lookup scan station.

const STAT_COLORS = {
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-700',
  gray: 'bg-gray-50 border-gray-200 text-gray-700',
  red: 'bg-red-50 border-red-200 text-red-700',
};

const SummaryStat = ({ label, value, color }) => (
  <div className={`rounded-lg py-3 border ${STAT_COLORS[color] || STAT_COLORS.gray}`}>
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

const DealerParcelImportModal = ({ open, onClose, shipDateId }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const fileInputRef = useRef(null);

  // Start fresh every time it reopens — clear the picked file + last summary.
  const handleClose = () => {
    if (importing) return; // don't close mid-import
    setFile(null);
    setSummary(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  if (!open) return null;

  const handleImport = async () => {
    if (!file) { toast.warning('เลือกไฟล์ Excel ก่อน'); return; }
    setImporting(true);
    setSummary(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Scope the import to the round the operator selected, so a date under both D2U and
      // ODA J4410 resolves to the RIGHT warehouse instead of an arbitrary one.
      if (shipDateId) fd.append('shipDateId', shipDateId);
      const res = await WeeklyShipService.dealerImportAir(fd);
      setSummary(res.data);
      toast.success(`นำเข้าแล้ว — สร้าง ${res.data.created} / พัก ${res.data.held} / ข้าม ${res.data.skipped} / ผิดพลาด ${res.data.errorCount}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'นำเข้าไม่สำเร็จ');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={handleClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold">นำเข้าพัสดุ Dealer (D2U)</h2>
            <p className="text-sm text-gray-500 mt-1">
              อัปโหลดไฟล์ parcelBox — จับคู่ออเดอร์ + ตั้งราคาอัตโนมัติ, ที่เหลือพักไว้. สแกน QR เพื่อพิมพ์ label ที่หน้าสแกน Tracking JP.
            </p>
          </div>
          <button onClick={handleClose} disabled={importing} className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-xl leading-none">×</button>
        </div>

        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            disabled={importing}
            onChange={(e) => { setFile(e.target.files?.[0] || null); setSummary(null); }}
            className="text-sm"
          />
          <button
            onClick={handleImport}
            disabled={importing || !file}
            className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {importing ? 'กำลังนำเข้า…' : 'นำเข้า'}
          </button>
        </div>

        {importing && (
          <div className="mt-4">
            <LinearProgress />
            <p className="text-xs text-gray-400 mt-1">กำลังนำเข้าและจับคู่ออเดอร์… (รูปภาพจะอัปโหลดเบื้องหลัง)</p>
          </div>
        )}

        {summary && (
          <div className="mt-4 grid grid-cols-4 gap-2 text-center">
            <SummaryStat label="สร้างกล่อง" value={summary.created} color="emerald" />
            <SummaryStat label="พักไว้" value={summary.held} color="amber" />
            <SummaryStat label="ข้าม" value={summary.skipped} color="gray" />
            <SummaryStat label="ผิดพลาด" value={summary.errorCount} color="red" />
          </div>
        )}
        {summary?.errors?.length > 0 && (
          <div className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 space-y-1 max-h-40 overflow-y-auto">
            {summary.errors.map((e, i) => (
              <div key={i}><span className="font-mono">{e.boxId}</span> — {e.error}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DealerParcelImportModal;
